import { useState, useEffect, useCallback } from "react";
import { Select } from "antd";
import { useOutletContext } from "react-router-dom";
import { menus, menuGroups } from "../../../../services/admin";
import type { MenuItem, MenuGroupItem } from "../../../../services/admin";
import { ensurePermission } from "../../../../utils/permissions";
import RIcon from "../../../../components/RIcon";

const MENU_ICON_OPTIONS = [
  "ri-home-line",
  "ri-dashboard-line",
  "ri-folder-line",
  "ri-folder-open-line",
  "ri-file-list-3-line",
  "ri-file-chart-line",
  "ri-chat-ai-line",
  "ri-settings-4-line",
  "ri-user-settings-line",
  "ri-shield-user-line",
  "ri-building-line",
  "ri-tools-line",
  "ri-node-tree",
  "ri-lock-password-line",
  "ri-global-line",
  "ri-bar-chart-box-line",
  "ri-database-2-line",
  "ri-microscope-line",
  "ri-article-line",
  "ri-calendar-line",
  "ri-links-line",
  "ri-book-open-line",
  "ri-notification-3-line",
  "ri-layout-grid-line",
];

type TreeNode = {
  id: string;
  name: string;
  icon: string;
  path: string;
  sort: number;
  enabled: boolean;
  isGroup?: boolean;
  children?: TreeNode[];
};

const statusStyle: Record<string, string> = {
  启用: "bg-emerald-100 text-emerald-700",
  停用: "bg-gray-100 text-gray-500",
};

function MenuRow({
  item,
  depth = 0,
  onEdit,
  onEditGroup,
}: {
  item: TreeNode;
  depth?: number;
  onEdit: (item: TreeNode) => void;
  onEditGroup: (item: TreeNode) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = item.children && item.children.length > 0;
  const statusLabel = item.enabled ? "启用" : "停用";

  return (
    <>
      <tr className="border-b border-gray-50 hover:bg-[#F4F8FB]/40 transition-colors group">
        <td className="px-4 py-3">
          <div
            className="flex items-center gap-2"
            style={{ paddingLeft: `${depth * 20}px` }}
          >
            {hasChildren ? (
              <button
                onClick={() => setExpanded(!expanded)}
                className="w-5 h-5 flex items-center justify-center text-[#718096] hover:text-[#0EA5A0] cursor-pointer flex-shrink-0"
              >
                <i
                  className={`ri-arrow-${
                    expanded ? "down" : "right"
                  }-s-line text-sm`}
                />
              </button>
            ) : (
              <span className="w-5 h-5 flex items-center justify-center text-gray-300 flex-shrink-0">
                <RIcon name="corner-down-right-line" className="text-xs" />
              </span>
            )}
            <span className="text-sm font-medium text-[#1A2B3C] whitespace-nowrap">
              {item.name}
            </span>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="w-7 h-7 flex items-center justify-center bg-[#F4F8FB] rounded-lg">
            <i className={`${item.icon} text-[#0EA5A0] text-sm`} />
          </div>
        </td>
        <td className="px-4 py-3 text-[#4A5568] text-center">{item.sort}</td>
        <td className="px-4 py-3">
          <span
            className={`inline-flex min-w-[3.5rem] items-center justify-center text-xs px-2 py-1 rounded-full ${statusStyle[statusLabel]}`}
          >
            {statusLabel}
          </span>
        </td>
        <td className="px-4 py-3">
          <button
            type="button"
            aria-label={item.isGroup ? "编辑分组" : "编辑菜单"}
            title={item.isGroup ? "编辑分组" : "编辑菜单"}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-[#718096] hover:text-[#0EA5A0] cursor-pointer"
            onClick={(ev) => {
              ev.stopPropagation();
              if (item.isGroup) {
                onEditGroup(item);
              } else {
                onEdit(item);
              }
            }}
          >
            <RIcon name="edit-line" className="text-sm" />
          </button>
        </td>
      </tr>
      {hasChildren &&
        expanded &&
        item.children!.map((child) => (
          <MenuRow
            key={child.id}
            item={child}
            depth={depth + 1}
            onEdit={onEdit}
            onEditGroup={onEditGroup}
          />
        ))}
    </>
  );
}

function IconPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (icon: string) => void;
}) {
  const activeIcon = value || "ri-file-line";
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-[#F8FBFD] px-3 py-2">
        <div className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-[#0EA5A0]">
          <i className={`${activeIcon} text-base`} />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-medium text-[#1A2B3C]">当前图标</div>
          <div className="text-xs text-[#718096] font-mono">{activeIcon}</div>
        </div>
      </div>
      <div className="grid grid-cols-6 gap-2 max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2">
        {MENU_ICON_OPTIONS.map((icon) => {
          const active = value === icon;
          return (
            <button
              key={icon}
              type="button"
              title={icon}
              onClick={() => onChange(icon)}
              className={`h-10 rounded-lg border flex items-center justify-center transition-colors cursor-pointer ${
                active
                  ? "border-[#0EA5A0] bg-[#0EA5A0]/10 text-[#0EA5A0]"
                  : "border-gray-200 text-[#718096] hover:border-[#0EA5A0]/50 hover:text-[#0EA5A0]"
              }`}
            >
              <i className={`${icon} text-base`} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function buildTree(groups: MenuGroupItem[], menuItems: MenuItem[]): TreeNode[] {
  const ungrouped: TreeNode[] = [];
  const groupMap = new Map<number, TreeNode>();

  for (const g of groups) {
    groupMap.set(g.id, {
      id: `group-${g.id}`,
      name: g.name,
      icon: g.icon || "ri-folder-line",
      path: "",
      sort: g.sort,
      enabled: true,
      isGroup: true,
      children: [],
    });
  }

  for (const m of menuItems) {
    const node: TreeNode = {
      id: `menu-${m.id}`,
      name: m.name,
      icon: m.icon || "ri-file-line",
      path: m.path,
      sort: m.sort,
      enabled: m.enabled,
    };
    if (m.group_id !== null && groupMap.has(m.group_id)) {
      groupMap.get(m.group_id)!.children!.push(node);
    } else {
      ungrouped.push(node);
    }
  }

  for (const g of groupMap.values()) {
    g.children!.sort((a, b) => a.sort - b.sort);
  }

  const groupNodes = Array.from(groupMap.values()).sort((a, b) => a.sort - b.sort);
  const sortedUngrouped = ungrouped.sort((a, b) => a.sort - b.sort);
  // 选择"无分组"的菜单用独立一组展示，每个独立菜单自成一组，避免混在列表末尾看不出归属
  const independentGroups: TreeNode[] = sortedUngrouped.map((node) => ({
    id: `group-indep-${node.id}`,
    name: node.name,
    icon: node.icon || "ri-folder-line",
    path: "",
    sort: node.sort + 10000,
    enabled: true,
    isGroup: true,
    children: [node],
  }));

  return [...groupNodes, ...independentGroups];
}

export default function MenusPage() {
  const { user } = useOutletContext<{
    user: import("../../../../services/auth").UserInfo | null;
  }>();
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editGroupOpen, setEditGroupOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [editGroupId, setEditGroupId] = useState<number | null>(null);
  const [groups, setGroups] = useState<MenuGroupItem[]>([]);
  const [editForm, setEditForm] = useState({
    name: "",
    path: "#",
    icon: "",
    group_id: "",
    sort: "0",
    enabled: true,
  });
  const [editGroupForm, setEditGroupForm] = useState({
    name: "",
    icon: "ri-folder-line",
    sort: "0",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editGroupLoading, setEditGroupLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const loadData = useCallback(async () => {
    try {
      setPageLoading(true);
      const [groupData, menuData] = await Promise.all([
        menuGroups.list(),
        menus.list(),
      ]);
      setGroups(groupData);
      setTree(buildTree(groupData, menuData));
    } catch (err) {
      showToast("加载菜单数据失败");
      console.error("Failed to load menus:", err);
    } finally {
      setPageLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const validateMenuForm = (
    formState: typeof editForm = editForm
  ) => {
    const errs: Record<string, string> = {};
    if (!formState.name.trim()) errs.name = "请输入菜单名称";
    return errs;
  };

  const handleOpenEdit = (item: TreeNode) => {
    if (!ensurePermission(user, "menus:write", showToast)) return;
    const rawId = Number(String(item.id).replace("menu-", ""));
    if (Number.isNaN(rawId)) return;
    const parentGroup = tree.find((node) =>
      node.children?.some((child) => child.id === item.id)
    );
    // 打开菜单项编辑时，先关闭可能残留的分组编辑态，避免两个弹窗叠加
    setEditGroupOpen(false);
    setEditGroupId(null);
    setEditId(rawId);
    setEditForm({
      name: item.name,
      path: item.path,
      icon: item.icon,
      group_id: parentGroup ? parentGroup.id.replace("group-", "") : "",
      sort: String(item.sort),
      enabled: item.enabled,
    });
    setErrors({});
    setEditOpen(true);
  };

  const handleOpenGroupEdit = (item: TreeNode) => {
    if (!ensurePermission(user, "menus:write", showToast)) return;
    const rawId = Number(String(item.id).replace("group-", ""));
    if (Number.isNaN(rawId)) return;
    // 打开分组编辑时，先关闭可能残留的菜单项编辑态，避免两个弹窗叠加
    setEditOpen(false);
    setEditId(null);
    setEditGroupId(rawId);
    setEditGroupForm({
      name: item.name,
      icon: item.icon || "ri-folder-line",
      sort: String(item.sort),
    });
    setErrors({});
    setEditGroupOpen(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    if (!ensurePermission(user, "menus:write", showToast)) return;
    e.preventDefault();
    const errs = validateMenuForm(editForm);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    if (editId === null) return;
    setEditLoading(true);
    try {
      const trimmedGroupId = (editForm.group_id || "").trim();
      const parsedGroupId =
        trimmedGroupId === "" ? null : Number(trimmedGroupId);
      // 显式指定 group_id 字段（含 null），保证后端 Pydantic model_fields_set 能识别“清空分组”意图
      await menus.update(editId, {
        name: editForm.name,
        path: editForm.path,
        icon: editForm.icon || undefined,
        group_id: Number.isNaN(parsedGroupId as number) ? null : parsedGroupId,
        sort: Number(editForm.sort) || 0,
        enabled: editForm.enabled,
      });
      setEditOpen(false);
      setEditId(null);
      showToast("菜单已更新");
      await loadData();
    } catch (err) {
      showToast("更新失败，请重试");
      console.error("Failed to update menu:", err);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditGroup = async (e: React.FormEvent) => {
    if (!ensurePermission(user, "menus:write", showToast)) return;
    e.preventDefault();
    if (editGroupId === null) return;
    if (!editGroupForm.name.trim()) {
      setErrors({ name: "请输入分组名称" });
      return;
    }
    setEditGroupLoading(true);
    try {
      await menuGroups.update(editGroupId, {
        name: editGroupForm.name,
        icon: editGroupForm.icon || undefined,
        sort: Number(editGroupForm.sort) || 0,
      });
      setEditGroupOpen(false);
      setEditGroupId(null);
      setErrors({});
      showToast("菜单分组已更新");
      await loadData();
    } catch (err) {
      showToast("更新分组失败，请重试");
      console.error("Failed to update menu group:", err);
    } finally {
      setEditGroupLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-[#1A2B3C] text-white text-sm px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-2">
          <RIcon name="checkbox-circle-line" className="text-[#AAFF45]" />
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#1A2B3C]">菜单管理</h2>
          <p className="text-xs text-[#718096] mt-0.5">
            管理系统导航菜单结构、图标和权限配置
          </p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-2">
        <RIcon name="drag-move-line" className="text-amber-600 flex-shrink-0" />
        <p className="text-amber-700 text-sm">
          菜单项与分组均支持编辑，可修改名称、图标、分组、排序和状态
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-[#F4F8FB]">
                {["菜单名称", "图标", "排序", "状态", "操作"].map((h) => (
                  <th
                    key={h}
                    className="text-left px-4 py-3 text-xs font-semibold text-[#718096] whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-[#718096]">
                    <RIcon name="loader-4-line" className="animate-spin text-lg mr-2" />
                    加载中...
                  </td>
                </tr>
              ) : tree.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-[#718096]">
                    暂无菜单数据
                  </td>
                </tr>
              ) : (
                tree.map((item) => (
                  <MenuRow
                    key={item.id}
                    item={item}
                    depth={0}
                    onEdit={handleOpenEdit}
                    onEditGroup={handleOpenGroupEdit}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#1A2B3C]">编辑菜单</h3>
              <button
                onClick={() => setEditOpen(false)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <RIcon name="close-line" className="text-[#718096]" />
              </button>
            </div>
            <form onSubmit={handleEdit} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">
                    菜单名称
                  </label>
                  <input
                    type="text"
                    className={`w-full border rounded-lg px-3 h-10 text-sm outline-none ${
                      errors.name
                        ? "border-red-400"
                        : "border-gray-200 focus:border-[#0EA5A0]"
                    }`}
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, name: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">
                    图标
                  </label>
                  <IconPicker
                    value={editForm.icon}
                    onChange={(icon) =>
                      setEditForm({ ...editForm, icon })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">
                    所属分组
                  </label>
                  <Select
                    showSearch
                    allowClear
                    style={{ width: "100%" }}
                    value={editForm.group_id || undefined}
                    onChange={(v) => setEditForm({ ...editForm, group_id: (v ?? "") as string })}
                    optionFilterProp="label"
                    placeholder="无分组"
                    options={groups.map((g) => ({ value: String(g.id), label: g.name }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">
                    排序
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
                    value={editForm.sort}
                    onChange={(e) =>
                      setEditForm({ ...editForm, sort: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">
                    状态
                  </label>
                  <div className="h-10 flex items-center justify-between gap-4 border border-gray-200 rounded-lg px-3">
                    <span className="text-sm font-medium text-[#1A2B3C] whitespace-nowrap ml-2.5">
                      {editForm.enabled ? "启用" : "停用"}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setEditForm({ ...editForm, enabled: !editForm.enabled })
                      }
                      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors cursor-pointer ${
                        editForm.enabled ? "bg-[#0EA5A0]" : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          editForm.enabled ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setEditOpen(false)}
                  className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={editLoading}
                  className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  {editLoading ? (
                    <>
                      <RIcon name="loader-4-line" className="animate-spin" />
                      保存中...
                    </>
                  ) : (
                    "保存修改"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editGroupOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#1A2B3C]">编辑菜单分组</h3>
              <button
                onClick={() => setEditGroupOpen(false)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <RIcon name="close-line" className="text-[#718096]" />
              </button>
            </div>
            <form onSubmit={handleEditGroup} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">
                  分组名称
                </label>
                <input
                  type="text"
                  className={`w-full border rounded-lg px-3 h-10 text-sm outline-none ${
                    errors.name
                      ? "border-red-400"
                      : "border-gray-200 focus:border-[#0EA5A0]"
                  }`}
                  value={editGroupForm.name}
                  onChange={(e) =>
                    setEditGroupForm({ ...editGroupForm, name: e.target.value })
                  }
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">
                    图标
                  </label>
                  <IconPicker
                    value={editGroupForm.icon}
                    onChange={(icon) =>
                      setEditGroupForm({ ...editGroupForm, icon })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">
                    排序
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
                    value={editGroupForm.sort}
                    onChange={(e) =>
                      setEditGroupForm({ ...editGroupForm, sort: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setEditGroupOpen(false)}
                  className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={editGroupLoading}
                  className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  {editGroupLoading ? (
                    <>
                      <RIcon name="loader-4-line" className="animate-spin" />
                      保存中...
                    </>
                  ) : (
                    "保存分组"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
